import React, { useState, useContext, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, ActivityIndicator,
  Modal, TouchableOpacity, ScrollView
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AuthContext } from '../context/AuthContext';
import { useFocusEffect } from "@react-navigation/native";
import CustomAlert from '../components/CustomAlert';
import CONFIG from '../config/Config';

// ─── Sabitler ─────────────────────────────────────────────────────────────────

const PAYMENT_TYPES = [
  { id: 0,  name: "CARİ",        color: "#FF9800" },
  { id: 3,  name: "KREDİ KARTI", color: "#2196F3" },
  { id: 43, name: "NAKİT",       color: "#4CAF50" },
  { id: 48, name: "HAVALE",      color: "#9C27B0" },
];

const INITIAL_ALERT = {
  visible: false, title: '', message: '', type: 'success',
  showCancel: false, confirmText: 'Tamam', cancelText: 'Vazgeç',
  onConfirm: null, onCancel: null
};

// ─── Yardımcı Fonksiyonlar ────────────────────────────────────────────────────

const getPaymentColor = (kasaId) =>
  PAYMENT_TYPES.find(p => p.id === kasaId)?.color ?? "#7f8c8d";

const formatTL = (value) => {
  const number = parseFloat(value) || 0;
  return number.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " ₺";
};

const formatMiktar = (miktar) => {
  const m = parseFloat(miktar);
  if (m % 1 === 0) return `${m.toFixed(0)} adet`;
  if (m < 1)       return `${(m * 1000).toFixed(0)} g`;
  return `${m.toFixed(3).replace(/0+$/, '')} kg`;
};

// ─── Ürün Satırı ──────────────────────────────────────────────────────────────

const UrunSatiri = ({ urun, index }) => (
  <View style={[urunStyles.row, index % 2 === 1 && urunStyles.rowAlt]}>
    <View style={urunStyles.nameCol}>
      <Text style={urunStyles.name} numberOfLines={2}>{urun.urun_ad}</Text>
      <Text style={urunStyles.sub}>
        {formatMiktar(urun.miktar)}  ·  {formatTL(urun.fiyat)}
      </Text>
    </View>
    <Text style={urunStyles.tutar}>{formatTL(urun.tutar)}</Text>
  </View>
);

// ─── Bilgi Satırı ─────────────────────────────────────────────────────────────

const InfoRow = ({ label, value, valueStyle, last }) => (
  <View style={[detailStyles.infoRow, !last && detailStyles.infoRowBorder]}>
    <Text style={detailStyles.infoLabel}>{label}</Text>
    <Text style={[detailStyles.infoValue, valueStyle]} numberOfLines={2}>{value}</Text>
  </View>
);

// ─── Detay Modal ──────────────────────────────────────────────────────────────

const DetailModal = ({
  item, onClose, onPaymentChange, onGeriCagir, onIptal, onFisYazdir,
  userLevel, cariler, dateOffset
}) => {
  const [odemeSeciciAcik, setOdemeSeciciAcik] = React.useState(false);

  if (!item) return null;

  const isTahsilat    = item.KayitTipi === "Tahsilat";
  const borderColor   = isTahsilat ? "#8e44ad" : getPaymentColor(item.Kasa);
  const netTutar      = parseFloat(item.Tutar || 0) - parseFloat(item.IndirimMiktari || 0);
  const hasDiscount   = !isTahsilat && parseFloat(item.IndirimMiktari) > 0;
  const isGecmisTarih = dateOffset < 0;
  const canGeriCagir  = userLevel >= 4 && !isTahsilat && !isGecmisTarih;
  const canIptal      = userLevel >= 4 && !isGecmisTarih;
  const urunler       = item.urunler || [];
  const urunToplamTutar = urunler.reduce((sum, u) => sum + parseFloat(u.tutar || 0), 0);

  const getKullanilabilirOdemeYontemleri = () => {
    const aktifCari = cariler?.find(c => c.CariID == item.musteri_cari_id);

    if (aktifCari?.OdemeYotemleri) {
      const izinliYontemler = aktifCari.OdemeYotemleri.split(',').map(y => y.trim());
      const API_KEY_MAP = {
        "CARİ":        "CARI",
        "KREDİ KARTI": "KREDI_KARTI",
        "NAKİT":       "NAKIT",
        "HAVALE":      "HAVALE"
      };
      return PAYMENT_TYPES.filter(type => {
        if (type.id === item.Kasa) return false;
        const yontemKey = API_KEY_MAP[type.name] || type.name;
        if (yontemKey === "CARI" && aktifCari.AcikHesap == 0) return false;
        return izinliYontemler.includes(yontemKey);
      });
    }

    return PAYMENT_TYPES.filter(type => {
      if (type.id === item.Kasa) return false;
      const aktifCariDefault = cariler?.find(c => c.CariID == item.musteri_cari_id);
      if (type.name === "CARİ" && aktifCariDefault?.AcikHesap == 0) return false;
      return true;
    });
  };

  const kullanilabilirOdemeler = isGecmisTarih ? [] : getKullanilabilirOdemeYontemleri();

  return (
    <Modal animationType="slide" transparent visible onRequestClose={onClose}>
      <View style={detailStyles.overlay}>
        <View style={detailStyles.sheet}>

          {/* Üst Bant */}
          <View style={[detailStyles.topBand, { backgroundColor: borderColor }]}>
            <Text style={detailStyles.bandTitle}>
              {isTahsilat ? "Cari Tahsilat Detayı" : `Adisyon #${item.id}`}
            </Text>
            <TouchableOpacity onPress={onClose} style={detailStyles.closeBtn}>
              <Text style={detailStyles.closeBtnText}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView contentContainerStyle={detailStyles.body} showsVerticalScrollIndicator={false}>

            {/* Tutar Kutusu */}
            <View style={[detailStyles.amountBox, { borderColor }]}>
              <Text style={[detailStyles.amountValue, { color: borderColor }]}>
                {formatTL(netTutar)}
              </Text>
              {hasDiscount && (
                <View style={detailStyles.discountRow}>
                  <Text style={detailStyles.discountOld}>{formatTL(item.Tutar)}</Text>
                  <View style={detailStyles.discountBadge}>
                    <Text style={detailStyles.discountBadgeText}>
                      %{item.IndirimOrani} — -{formatTL(item.IndirimMiktari)}
                    </Text>
                  </View>
                </View>
              )}
            </View>

            {/* Genel Bilgiler */}
            <View style={detailStyles.infoCard}>
              <InfoRow label="Müşteri"  value={item.musteri_adi || "Genel Müşteri"} />
              <InfoRow label="Kasiyer"  value={item.KasiyerName || "Bilinmiyor"} />
              <InfoRow label="Tarih"    value={item.tarih_format || "—"} />

              {/* Ödeme Türü */}
              <View style={detailStyles.infoRow}>
                <Text style={detailStyles.infoLabel}>Ödeme Türü</Text>
                {!isTahsilat && kullanilabilirOdemeler.length > 0 ? (
                  <TouchableOpacity
                    style={detailStyles.odemeSeciciBtn}
                    onPress={() => setOdemeSeciciAcik(prev => !prev)}
                  >
                    <Text style={[detailStyles.odemeSeciciValue, { color: borderColor }]}>
                      {item.kasa_adi?.toUpperCase() || "—"}
                    </Text>
                    <Text style={{ color: borderColor, fontSize: 11, marginLeft: 6 }}>
                      {odemeSeciciAcik ? "▲" : "▼"}
                    </Text>
                  </TouchableOpacity>
                ) : (
                  <Text style={[detailStyles.odemeSeciciValue, { color: borderColor }]}>
                    {item.kasa_adi?.toUpperCase() || "—"}
                  </Text>
                )}
              </View>

              {/* İnline Ödeme Seçici */}
              {!isTahsilat && odemeSeciciAcik && kullanilabilirOdemeler.length > 0 && (
                <View style={detailStyles.inlineOdemeRow}>
                  {kullanilabilirOdemeler.map((type) => (
                    <TouchableOpacity
                      key={type.id}
                      style={[detailStyles.inlineTag, { borderColor: type.color }]}
                      onPress={() => {
                        setOdemeSeciciAcik(false);
                        onPaymentChange(type.id);
                      }}
                    >
                      <Text style={[detailStyles.inlineTagText, { color: type.color }]}>
                        {type.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              {!isTahsilat && odemeSeciciAcik && kullanilabilirOdemeler.length === 0 && (
                <View style={detailStyles.noOdemeBox}>
                  <Text style={detailStyles.noOdemeText}>
                    Bu cari için başka ödeme yöntemi tanımlı değil.
                  </Text>
                </View>
              )}
            </View>

            {/* Not */}
            {item.adisyon_notu ? (
              <View style={detailStyles.noteBox}>
                <Text style={detailStyles.noteText}>💬 {item.adisyon_notu}</Text>
              </View>
            ) : null}

            {/* Ürün Listesi */}
            {!isTahsilat && (
              <View style={detailStyles.section}>
                <View style={detailStyles.sectionHeader}>
                  <Text style={detailStyles.sectionTitle}>Satılan Ürünler</Text>
                  {urunler.length > 0 && (
                    <View style={[detailStyles.countBadge, { backgroundColor: borderColor }]}>
                      <Text style={detailStyles.countBadgeText}>{urunler.length} kalem</Text>
                    </View>
                  )}
                </View>

                {urunler.length === 0 ? (
                  <Text style={detailStyles.emptyUrun}>Ürün bilgisi bulunamadı.</Text>
                ) : (
                  <View style={urunStyles.table}>
                    <View style={urunStyles.tableHeader}>
                      <Text style={[urunStyles.headerCell, { flex: 1, textAlign: 'left' }]}>Ürün</Text>
                      <Text style={urunStyles.headerCell}>Tutar</Text>
                    </View>
                    {urunler.map((urun, index) => (
                      <UrunSatiri key={urun.id} urun={urun} index={index} />
                    ))}
                    <View style={urunStyles.totalRow}>
                      <Text style={urunStyles.totalLabel}>
                        Toplam ({urunler.length} ürün)
                      </Text>
                      <Text style={[urunStyles.totalValue, { color: borderColor }]}>
                        {formatTL(urunToplamTutar)}
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            )}

            {/* Aksiyonlar */}
            <View style={detailStyles.actionRow}>
              {!isTahsilat && (
                <TouchableOpacity
                  style={[detailStyles.actionBtn, { backgroundColor: "#e67e22" }]}
                  onPress={() => onFisYazdir(item)}
                >
                  <Text style={detailStyles.actionBtnText}>🖨 Yazdır</Text>
                </TouchableOpacity>
              )}
              {canGeriCagir && (
                <TouchableOpacity
                  style={[detailStyles.actionBtn, { backgroundColor: "#27ae60" }]}
                  onPress={() => onGeriCagir(item)}
                >
                  <Text style={detailStyles.actionBtnText}>↩ Geri Çağır</Text>
                </TouchableOpacity>
              )}
              {canIptal && (
                <TouchableOpacity
                  style={[detailStyles.actionBtn, { backgroundColor: "#c0392b" }]}
                  onPress={() => onIptal(item)}
                >
                  <Text style={detailStyles.actionBtnText}>✕ İptal Et</Text>
                </TouchableOpacity>
              )}
            </View>

            <TouchableOpacity style={detailStyles.dismissBtn} onPress={onClose}>
              <Text style={detailStyles.dismissText}>Kapat</Text>
            </TouchableOpacity>

          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

// ─── Kart Bileşeni ────────────────────────────────────────────────────────────

const ReportCard = ({ item, onPress }) => {
  const isTahsilat  = item.KayitTipi === "Tahsilat";
  const borderColor = isTahsilat ? "#8e44ad" : getPaymentColor(item.Kasa);
  const netTutar    = parseFloat(item.Tutar || 0) - parseFloat(item.IndirimMiktari || 0);
  const hasDiscount = !isTahsilat && parseFloat(item.IndirimMiktari) > 0;
  const urunSayisi  = item.urunler?.length ?? 0;

  return (
    <TouchableOpacity
      style={[styles.card, { borderLeftColor: borderColor }]}
      onPress={() => onPress(item)}
      activeOpacity={0.75}
    >
      <View style={styles.topRow}>
        <View style={{ flex: 1, marginRight: 10 }}>
          <Text style={styles.customer} numberOfLines={2} ellipsizeMode="tail">
            {item.musteri_adi || "Genel Müşteri"}
          </Text>
          <Text style={styles.date}>{item.tarih_format}</Text>
        </View>
        <View style={{ alignItems: 'flex-end', minWidth: 100 }}>
          <View style={[styles.paymentBadge, { backgroundColor: borderColor }]}>
            <Text style={styles.paymentText}>{item.kasa_adi?.toUpperCase()}</Text>
          </View>
          <Text style={styles.cashierNameRight} numberOfLines={1}>
            👤 {item.KasiyerName || "Bilinmiyor"}
          </Text>
        </View>
      </View>

      <Text style={[styles.amount, isTahsilat && { color: "#8e44ad" }]}>
        {formatTL(netTutar)}
      </Text>

      {hasDiscount && (
        <View style={styles.discountBox}>
          <Text style={styles.discountMain}>%{item.IndirimOrani} İndirim</Text>
          <Text style={styles.discountSub}>-{formatTL(item.IndirimMiktari)}</Text>
          <Text style={styles.oldPrice}>{formatTL(item.Tutar)}</Text>
        </View>
      )}

      {item.adisyon_notu ? (
        <Text style={styles.note} numberOfLines={1}>💬 {item.adisyon_notu}</Text>
      ) : null}

      <View style={styles.bottomRow}>
        <View style={[styles.idBadge, isTahsilat && { backgroundColor: "#f3e5f5" }]}>
          <Text style={[styles.idText, isTahsilat && { color: "#8e44ad" }]}>
            {isTahsilat ? "Cari Tahsilat" : `Adisyon #${item.id}`}
          </Text>
        </View>
        <View style={styles.bottomRight}>
          {!isTahsilat && urunSayisi > 0 && (
            <View style={[styles.urunCountBadge, { backgroundColor: borderColor + '20', borderColor }]}>
              <Text style={[styles.urunCountText, { color: borderColor }]}>
                🛒 {urunSayisi} ürün
              </Text>
            </View>
          )}
          <Text style={[styles.detailHint, { color: borderColor }]}>Detay →</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

// ─── Ana Ekran ────────────────────────────────────────────────────────────────

export default function RaporlarScreen({ navigation }) {
  const [loading,      setLoading]      = useState(true);
  const [raporlar,     setRaporlar]     = useState([]);
  const [cariler,      setCariler]      = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [alertConfig,  setAlertConfig]  = useState(INITIAL_ALERT);
  const [dateOffset,   setDateOffset]   = useState(0);

  const { userToken } = useContext(AuthContext);
  const userInfo      = userToken ? JSON.parse(userToken) : null;
  const userLevel     = userInfo?.Level || 0;

  const closeAlert  = useCallback(() =>
    setAlertConfig(prev => ({ ...prev, visible: false })), []);
  const closeDetail = useCallback(() => setSelectedItem(null), []);

  const getDateForOffset = useCallback((offset) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return d;
  }, []);

  const formatAPIDate = useCallback((offset) => {
    const d   = getDateForOffset(offset);
    const y   = d.getFullYear();
    const m   = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, [getDateForOffset]);

  const formatDisplayDate = useCallback((offset) => {
    if (offset === 0)  return 'Bugün';
    if (offset === -1) return 'Dün';
    const d      = getDateForOffset(offset);
    const MONTHS = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  }, [getDateForOffset]);

  const fetchRaporlar = useCallback(async (offset = dateOffset) => {
    setLoading(true);
    try {
      const tarih = formatAPIDate(offset);

      const [raporRes, cariRes] = await Promise.all([
        fetch(`${CONFIG.API_BASE_URL}/raporlar?tarih=${tarih}`, { headers: { 'X-App-Env': CONFIG.ENV } }),
        fetch(`${CONFIG.API_BASE_URL}/cari`, { headers: { 'X-App-Env': CONFIG.ENV } })
      ]);

      const raporJson = await raporRes.json();
      const cariJson  = await cariRes.json();

      if (Array.isArray(raporJson))                        setRaporlar(raporJson);
      else if (raporJson?.data && Array.isArray(raporJson.data)) setRaporlar(raporJson.data);
      else                                                  setRaporlar([]);

      let parsedCariler = [];
      if (Array.isArray(cariJson))                               parsedCariler = cariJson;
      else if (cariJson?.data    && Array.isArray(cariJson.data))    parsedCariler = cariJson.data;
      else if (cariJson?.cariler && Array.isArray(cariJson.cariler)) parsedCariler = cariJson.cariler;
      setCariler(parsedCariler);

    } catch (error) {
      console.error("Fetch hatası:", error);
    } finally {
      setLoading(false);
    }
  }, [dateOffset, formatAPIDate]);

  useFocusEffect(useCallback(() => {
    fetchRaporlar(dateOffset);
  }, [dateOffset]));

  // ── Tarih Navigasyonu ────────────────────────────────────────────────────────

  const goToPrevDay = useCallback(() => setDateOffset(prev => prev - 1), []);
  const goToNextDay = useCallback(() => setDateOffset(prev => Math.min(prev + 1, 0)), []);

  // ── Fiş Yazdır ───────────────────────────────────────────────────────────────

  const fisYazdir = useCallback(async (item) => {
    if (!item?.urunler?.length) {
      setAlertConfig({
        ...INITIAL_ALERT,
        visible: true, title: 'Hata',
        message: 'Bu adisyonda ürün bulunamadı!', type: 'error',
        onConfirm: closeAlert
      });
      return;
    }

    const tlFormat   = (num) => Number(num).toLocaleString('tr-TR', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
    const netTutar   = parseFloat(item.Tutar || 0) - parseFloat(item.IndirimMiktari || 0);
    const kasaAdi    = PAYMENT_TYPES.find(p => p.id === item.Kasa)?.name ?? item.kasa_adi ?? '—';

    const siparisPaketi = {
      adisyonNo:   item.id,
      odemeTuru:   kasaAdi,
      musteriAdi:  item.musteri_adi  || 'Genel Müşteri',
      kasiyer:     item.KasiyerName  || 'Bilinmiyor',
      tarih:       item.tarih_format || new Date().toLocaleString('tr-TR'),
      urunler:     item.urunler.map(u => ({
        ad:    u.urun_ad,
        detay: `${formatMiktar(u.miktar)} X ${tlFormat(u.fiyat)} TL`,
        tutar: `${tlFormat(u.tutar)} TL`,
      })),
      toplamTutar: `${tlFormat(netTutar)} TL`,
    };

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/yazdir_rapor`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-App-Env': CONFIG.ENV },
        body:    JSON.stringify(siparisPaketi),
      });
        const rawText = await response.text(); // ← JSON.parse yerine önce text al
        //console.log('YAZDIR RAW RESPONSE:', rawText);
        //console.log('STATUS:', response.status);

        const result = JSON.parse(rawText);

      if (response.ok && result.success) {
        setAlertConfig({
          ...INITIAL_ALERT,
          visible: true, title: 'Başarılı!',
          message: 'Fiş başarıyla yazdırıldı.', type: 'success',
          onConfirm: closeAlert
        });
      } else {
        throw new Error(result.message || 'Sunucu hatası oluştu.');
      }
    } catch (error) {
      setAlertConfig({
        ...INITIAL_ALERT,
        visible: true, title: 'Eyvah!',
        message: 'Yazıcıya ulaşılamadı: ' + error.message,
        type: 'error', showCancel: true,
        confirmText: 'Tekrar Dene', cancelText: 'Kapat',
        onCancel: closeAlert,
        onConfirm: () => { closeAlert(); fisYazdir(item); }
      });
    }
  }, [closeAlert]);

  // ── Ödeme Türü Değiştir ──────────────────────────────────────────────────────

  const handleUpdatePayment = useCallback((yeniKasaId) => {
    if (!selectedItem) return;
    const kasaAdi = PAYMENT_TYPES.find(p => p.id === yeniKasaId)?.name ?? '';
    setAlertConfig({
      ...INITIAL_ALERT,
      visible: true,
      title:   "Emin misiniz?",
      message: `Adisyon #${selectedItem.id} ödeme türü "${kasaAdi}" olarak değiştirilecek.`,
      type: "warning", showCancel: true,
      onCancel:  closeAlert,
      onConfirm: () => executePaymentUpdate(yeniKasaId)
    });
  }, [selectedItem]);

  const executePaymentUpdate = useCallback(async (yeniKasaId) => {
    closeAlert();
    try {
      const res    = await fetch(`${CONFIG.API_BASE_URL}/odeme`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-App-Env': CONFIG.ENV },
        body: JSON.stringify({
          action:          "OdemeTuruDegistir",
          KasiyerID:       userInfo?.ID,
          KasiyerLevel:    userInfo?.Level,
          OdemeID:         selectedItem.Kasa,
          AdisyonID:       selectedItem.id,
          KapanmaTarihi:   selectedItem.kapanma_tarihi,
          MusteriID:       selectedItem.musteri_cari_id,
          YeniOdemeID:     yeniKasaId
        })
      });
      const result = await res.json();
      if (result.success) fetchRaporlar();
      setAlertConfig({
        ...INITIAL_ALERT,
        visible:  true,
        title:    result.success ? "İşlem Başarılı" : "İşlem Başarısız",
        type:     result.success ? "success" : "error",
        message:  result.message || "Ödeme türü güncellendi.",
        showCancel: false,
        onConfirm: () => { closeAlert(); if (result.success) closeDetail(); }
      });
    } catch {
      setAlertConfig({
        ...INITIAL_ALERT, visible: true, title: "Hata",
        message: "Güncellenirken bir sorun oluştu.", type: "error",
        onConfirm: closeAlert
      });
    }
  }, [selectedItem, userInfo]);

  // ── İptal ────────────────────────────────────────────────────────────────────

  const handleIptal = useCallback((item) => {
    const isTahsilat = item.KayitTipi === "Tahsilat";
    setAlertConfig({
      ...INITIAL_ALERT,
      visible: true,
      title:   isTahsilat ? "Tahsilat İptali" : "İptal Onayı",
      message: isTahsilat
        ? `${item.musteri_adi} adına ${item.tutar_tl} tutarındaki tahsilat iptal edilecek. Emin misiniz?`
        : `#${item.id} numaralı hareket iptal edilecek (Geri alınamaz). Emin misiniz?`,
      type: "warning", showCancel: true,
      onCancel:  closeAlert,
      onConfirm: () => isTahsilat ? executeTahsilatIptal(item) : executeIptal(item)
    });
  }, []);

  const executeIptal = useCallback(async (item) => {
    closeAlert();
    try {
      const res    = await fetch(`${CONFIG.API_BASE_URL}/odeme`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-App-Env': CONFIG.ENV },
        body: JSON.stringify({
          action:       "TahsilatIptal",
          KasiyerID:    userInfo?.ID,
          KasiyerLevel: userInfo?.Level,
          AdisyonID:    item.id
        })
      });
      const result = await res.json();
      setAlertConfig({
        ...INITIAL_ALERT,
        visible:  true,
        title:    result.success ? "İptal Edildi" : "İşlem Başarısız",
        message:  result.message || "Hareket iptal edildi.",
        type:     result.success ? "success" : "error",
        showCancel: false,
        onConfirm: () => { closeAlert(); if (result.success) { fetchRaporlar(); closeDetail(); } }
      });
    } catch {
      setAlertConfig({
        ...INITIAL_ALERT, visible: true, title: "Hata",
        message: "İptal sırasında sunucuya ulaşılamadı.", type: "error",
        onConfirm: closeAlert
      });
    }
  }, [userInfo]);

  const executeTahsilatIptal = useCallback(async (item) => {
    closeAlert();
    try {
      const res    = await fetch(`${CONFIG.API_BASE_URL}/cari_tahsilat_iptal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-App-Env': CONFIG.ENV },
        body: JSON.stringify({
          id:        parseInt(item.id.split('-')[1], 10),
          kasiyerId: userInfo?.ID,
          userLevel: userInfo?.Level
        })
      });
      const result = await res.json();
      setAlertConfig({
        ...INITIAL_ALERT,
        visible:  true,
        title:    result.success ? "Başarılı" : "Hata",
        message:  result.message || "İşlem tamamlandı.",
        type:     result.success ? "success" : "danger",
        showCancel: false,
        onConfirm: () => { closeAlert(); if (result.success) { fetchRaporlar(); closeDetail(); } }
      });
    } catch {
      console.error("Tahsilat iptal hatası");
    }
  }, [userInfo]);

  // ── Geri Çağır ───────────────────────────────────────────────────────────────

  const handleGeriCagir = useCallback((item) => {
    setAlertConfig({
      ...INITIAL_ALERT,
      visible: true,
      title:   "Geri Çağır Onayı",
      message: `Adisyon #${item.id} tekrar açık duruma getirilecek. Emin misiniz?`,
      type: "warning", showCancel: true,
      onCancel:  closeAlert,
      onConfirm: () => executeGeriCagir(item)
    });
  }, []);

  const executeGeriCagir = useCallback(async (item) => {
    closeAlert();
    try {
      const res    = await fetch(`${CONFIG.API_BASE_URL}/odeme`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-App-Env': CONFIG.ENV },
        body: JSON.stringify({
          action:       "AdisyonGeriCagir",
          KasiyerID:    userInfo?.ID,
          KasiyerLevel: userInfo?.Level,
          AdisyonID:    item.id
        })
      });
      const result = await res.json();
      setAlertConfig({
        ...INITIAL_ALERT,
        visible:  true,
        title:    result.success ? "Geri Çağrıldı" : "İşlem Başarısız",
        message:  result.message || "Adisyon geri çağrıldı.",
        type:     result.success ? "success" : "error",
        showCancel: false,
        onConfirm: () => {
          closeAlert();
          if (result.success) {
            fetchRaporlar();
            closeDetail();
            navigation.jumpTo('Ana Sayfa', { refreshAdisyonlar: Date.now() });
          }
        }
      });
    } catch {
      setAlertConfig({
        ...INITIAL_ALERT, visible: true, title: "Hata",
        message: "Geri çağırma sırasında sunucuya ulaşılamadı.", type: "error",
        onConfirm: closeAlert
      });
    }
  }, [userInfo, navigation]);

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <SafeAreaView style={styles.container} edges={['top', 'left', 'right']}>

      <View style={styles.header}>
        <Text style={styles.headerTitle}>Hareketler ({raporlar.length})</Text>
        {!loading && (
          <View style={styles.dateNav}>
            <TouchableOpacity
              style={styles.dateNavBtn}
              onPress={goToPrevDay}
              activeOpacity={0.7}
            >
              <Text style={styles.dateNavArrow}>‹</Text>
            </TouchableOpacity>

            <View style={styles.dateDisplay}>
              <Text style={styles.dateDisplayText}>
                {formatDisplayDate(dateOffset)}
              </Text>
            </View>

            <TouchableOpacity
              style={[styles.dateNavBtn, dateOffset >= 0 && styles.dateNavBtnDisabled]}
              onPress={goToNextDay}
              disabled={dateOffset >= 0}
              activeOpacity={0.7}
            >
              <Text style={[styles.dateNavArrow, dateOffset >= 0 && { opacity: 0.3 }]}>›</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {loading ? (
        <ActivityIndicator size="large" color="#c0392b" style={{ flex: 1 }} />
      ) : (
        <FlatList
          data={raporlar}
          keyExtractor={(item) => item.id.toString()}
          renderItem={({ item }) => (
            <ReportCard item={item} onPress={setSelectedItem} />
          )}
          contentContainerStyle={{ padding: 15 }}
          ListEmptyComponent={
            <Text style={styles.empty}>
              {formatDisplayDate(dateOffset)} tarihine ait kayıt bulunamadı.
            </Text>
          }
        />
      )}

      <DetailModal
        item={selectedItem}
        onClose={closeDetail}
        onPaymentChange={handleUpdatePayment}
        onGeriCagir={handleGeriCagir}
        onIptal={handleIptal}
        onFisYazdir={fisYazdir}
        userLevel={userLevel}
        cariler={cariler}
        dateOffset={dateOffset}
      />

      <CustomAlert
        visible={alertConfig.visible}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        showCancel={alertConfig.showCancel}
        confirmText={alertConfig.confirmText}
        cancelText={alertConfig.cancelText}
        onConfirm={alertConfig.onConfirm}
        onCancel={alertConfig.onCancel}
      />

    </SafeAreaView>
  );
}

// ─── Stiller ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container:          { flex: 1, backgroundColor: "#f4f6f7" },
  header:             { padding: 15, backgroundColor: "#fff", borderBottomWidth: 1,
                        borderBottomColor: "#ecf0f1", flexDirection: "row",
                        justifyContent: "space-between", alignItems: "center" },
  headerTitle:        { fontSize: 18, fontWeight: "700", color: "#2c3e50" },
  empty:              { textAlign: "center", marginTop: 20, color: "#7f8c8d" },

  // Kart
  card:               { backgroundColor: "#fff", borderRadius: 14, padding: 14,
                        marginBottom: 12, elevation: 3, borderLeftWidth: 5 },
  topRow:             { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  customer:           { fontSize: 16, fontWeight: "600", color: "#2c3e50" },
  date:               { fontSize: 12, color: "#7f8c8d", marginTop: 2 },
  amount:             { fontSize: 26, fontWeight: "700", color: "#2c3e50", marginVertical: 10 },
  paymentBadge:       { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20 },
  paymentText:        { color: "#fff", fontSize: 11, fontWeight: "700" },
  discountBox:        { backgroundColor: "#fff6f6", padding: 8, borderRadius: 10, marginBottom: 6 },
  discountMain:       { fontSize: 13, fontWeight: "700", color: "#c0392b" },
  discountSub:        { fontSize: 12, color: "#e74c3c", marginTop: 2 },
  oldPrice:           { fontSize: 11, color: "#7f8c8d", textDecorationLine: "line-through", marginTop: 2 },
  note:               { marginTop: 6, fontSize: 12, color: "#555", fontStyle: "italic" },
  bottomRow:          { flexDirection: "row", justifyContent: "space-between",
                        alignItems: "center", marginTop: 8 },
  idBadge:            { backgroundColor: "#ecf0f1", paddingHorizontal: 10, paddingVertical: 4,
                        borderRadius: 8, borderLeftWidth: 3 },
  idText:             { fontSize: 12, fontWeight: "600", color: "#34495e" },
  bottomRight:        { flexDirection: "row", alignItems: "center", gap: 8 },
  urunCountBadge:     { borderWidth: 1, borderRadius: 8, paddingHorizontal: 7, paddingVertical: 3 },
  urunCountText:      { fontSize: 11, fontWeight: "700" },
  detailHint:         { fontSize: 12, fontWeight: "700" },
  cashierNameRight:   { fontSize: 11, color: '#95a5a6', marginTop: 4, marginRight: 5,
                        fontWeight: '600', textTransform: 'capitalize' },

  // Tarih Nav
  dateNav:            { flexDirection: 'row', alignItems: 'center', gap: 6 },
  dateNavBtn:         { width: 30, height: 30, borderRadius: 15, backgroundColor: '#f0f0f0',
                        justifyContent: 'center', alignItems: 'center' },
  dateNavBtnDisabled: { opacity: 0.4 },
  dateNavArrow:       { fontSize: 18, color: '#2c3e50', fontWeight: '700', lineHeight: 20 },
  dateDisplay:        { paddingHorizontal: 10, paddingVertical: 5, backgroundColor: '#f4f6f7',
                        borderRadius: 8, minWidth: 70, alignItems: 'center' },
  dateDisplayText:    { fontSize: 12, fontWeight: '700', color: '#2c3e50' },
});

const detailStyles = StyleSheet.create({
  overlay:           { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet:             { backgroundColor: '#fff', borderTopLeftRadius: 20,
                       borderTopRightRadius: 20, maxHeight: '92%', overflow: 'hidden' },
  topBand:           { flexDirection: 'row', justifyContent: 'space-between',
                       alignItems: 'center', paddingHorizontal: 20, paddingVertical: 16 },
  bandTitle:         { fontSize: 17, fontWeight: '700', color: '#fff' },
  closeBtn:          { width: 30, height: 30, borderRadius: 15,
                       backgroundColor: 'rgba(255,255,255,0.3)',
                       justifyContent: 'center', alignItems: 'center' },
  closeBtnText:      { color: '#fff', fontWeight: '700', fontSize: 14 },
  body:              { padding: 20, paddingBottom: 44 },
  amountBox:         { borderWidth: 2, borderRadius: 14, padding: 16,
                       alignItems: 'center', marginBottom: 16 },
  amountValue:       { fontSize: 34, fontWeight: '800' },
  discountRow:       { flexDirection: 'row', alignItems: 'center', marginTop: 8, gap: 8, flexWrap: 'wrap' },
  discountOld:       { fontSize: 12, color: '#7f8c8d', textDecorationLine: 'line-through' },
  discountBadge:     { backgroundColor: '#ffeaea', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  discountBadgeText: { fontSize: 12, color: '#c0392b', fontWeight: '700' },
  infoCard:          { backgroundColor: '#f8f9fa', borderRadius: 12, padding: 14, marginBottom: 14 },
  infoRow:           { flexDirection: 'row', justifyContent: 'space-between',
                       alignItems: 'center', paddingVertical: 9 },
  infoRowBorder:     { borderBottomWidth: 1, borderBottomColor: '#ecf0f1' },
  infoLabel:         { fontSize: 13, color: '#7f8c8d', fontWeight: '500' },
  infoValue:         { fontSize: 13, color: '#2c3e50', fontWeight: '600',
                       textAlign: 'right', flex: 1, marginLeft: 12 },
  noteBox:           { backgroundColor: '#fffde7', borderRadius: 10, padding: 12, marginBottom: 14 },
  noteText:          { fontSize: 13, color: '#555', lineHeight: 20 },
  section:           { marginBottom: 18 },
  sectionHeader:     { flexDirection: 'row', alignItems: 'center',
                       justifyContent: 'space-between', marginBottom: 10 },
  sectionTitle:      { fontSize: 13, color: '#7f8c8d', fontWeight: '700',
                       textTransform: 'uppercase', letterSpacing: 0.5 },
  countBadge:        { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  countBadgeText:    { color: '#fff', fontSize: 11, fontWeight: '700' },
  emptyUrun:         { textAlign: 'center', color: '#aaa', fontSize: 13, paddingVertical: 12 },
  actionRow:         { flexDirection: 'row', gap: 10, marginBottom: 12 },
  actionBtn:         { flex: 1, paddingVertical: 13, borderRadius: 10,
                       alignItems: 'center', justifyContent: 'center' },
  actionBtnText:     { color: '#fff', fontWeight: '700', fontSize: 14 },
  dismissBtn:        { paddingVertical: 13, borderRadius: 10,
                       backgroundColor: '#f1f2f6', alignItems: 'center' },
  dismissText:       { color: '#7f8c8d', fontWeight: '700', fontSize: 14 },
  odemeSeciciBtn:    { flexDirection: 'row', alignItems: 'center', flexShrink: 1 },
  odemeSeciciValue:  { fontSize: 13, fontWeight: '700', textAlign: 'right' },
  inlineOdemeRow:    { flexDirection: 'row', flexWrap: 'wrap', gap: 8,
                       paddingTop: 6, paddingBottom: 10, paddingHorizontal: 4,
                       justifyContent: 'center' },
  inlineTag:         { borderWidth: 2, borderRadius: 16, paddingVertical: 6, paddingHorizontal: 14 },
  inlineTagText:     { fontWeight: '700', fontSize: 13 },
  noOdemeBox:        { backgroundColor: '#fff3cd', borderRadius: 8, padding: 10, marginTop: 8 },
  noOdemeText:       { fontSize: 12, color: '#856404', textAlign: 'center' },
});

const urunStyles = StyleSheet.create({
  table:       { borderRadius: 12, overflow: 'hidden', borderWidth: 1, borderColor: '#ecf0f1' },
  tableHeader: { flexDirection: 'row', backgroundColor: '#f4f6f7',
                 paddingVertical: 8, paddingHorizontal: 12 },
  headerCell:  { flex: 1, fontSize: 11, fontWeight: '700', color: '#7f8c8d',
                 textTransform: 'uppercase', textAlign: 'right' },
  row:         { flexDirection: 'row', paddingVertical: 11,
                 paddingHorizontal: 12, alignItems: 'center', backgroundColor: '#fff' },
  rowAlt:      { backgroundColor: '#fafbfc' },
  nameCol:     { flex: 1 },
  name:        { fontSize: 14, fontWeight: '700', color: '#2c3e50' },
  sub:         { fontSize: 12, color: '#7f8c8d', marginTop: 3 },
  tutar:       { fontSize: 14, fontWeight: '800', color: '#2c3e50', textAlign: 'right', minWidth: 80 },
  totalRow:    { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
                 paddingVertical: 10, paddingHorizontal: 12,
                 backgroundColor: '#f4f6f7', borderTopWidth: 1, borderTopColor: '#ddd' },
  totalLabel:  { fontSize: 13, fontWeight: '700', color: '#7f8c8d' },
  totalValue:  { fontSize: 15, fontWeight: '800' },
});